import { useStore } from 'jotai/react';
import { useEffect, useRef } from 'react';
import { stageRefAtom, stageViewportAtom } from '@/store/boardAtoms';
/**
 * Хук для управления видимостью элементов на Stage (виртуализация)
 *
 * Обновляет видимость элементов напрямую через Konva API для избежания перерендеринга React компонентов.
 * Во время активного pan/zoom обновляет видимость периодически для плавного появления элементов.
 *
 * @param params - Параметры хука
 */
export const useStageVisibility = ({ boardState, isPanning, isZooming, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    const visibilityUpdateIntervalRef = useRef(null);
    const lastVisibilityUpdateTimeRef = useRef(0);
    // Основной эффект для обновления видимости
    useEffect(() => {
        const stage = store.get(stageRefAtom);
        if (!stage)
            return;
        // Кэшируем карту элементов для быстрого доступа
        const elementMap = new Map();
        const updateElementMap = () => {
            elementMap.clear();
            for (const layer of boardState.layers || []) {
                if (layer.visible) {
                    for (const element of layer.elements || []) {
                        elementMap.set(element.id, element);
                    }
                }
            }
        };
        updateElementMap();
        /**
         * Обновляет видимость элементов на основе текущего viewport
         * Использует refs для получения актуальных значений, чтобы не зависеть от замыкания
         */
        const updateVisibility = () => {
            var _a, _b, _c, _d;
            const currentStage = store.get(stageRefAtom);
            const currentViewport = store.get(stageViewportAtom);
            if (!currentStage || !currentViewport)
                return;
            const currentStagePosition = currentViewport.position;
            const currentZoom = currentViewport.zoom;
            const currentStageSize = currentViewport.size;
            const margin = currentStageSize.width / 3; // Буфер = 1/3 ширины экрана
            const viewportLeft = -currentStagePosition.x / currentZoom;
            const viewportTop = -currentStagePosition.y / currentZoom;
            const viewportRight = viewportLeft + currentStageSize.width / currentZoom;
            const viewportBottom = viewportTop + currentStageSize.height / currentZoom;
            const bufferInWorld = margin / currentZoom;
            const searchLeft = viewportLeft - bufferInWorld;
            const searchTop = viewportTop - bufferInWorld;
            const searchRight = viewportRight + bufferInWorld;
            const searchBottom = viewportBottom + bufferInWorld;
            // Находим все элементы на Stage
            const elements = currentStage.find('.element');
            const visibilityUpdates = [];
            for (const node of elements) {
                // Получаем ID элемента из атрибута id ноды (формат: "node-{id}")
                const nodeId = node.id();
                if (!nodeId || !nodeId.startsWith('node-'))
                    continue;
                const elementId = parseInt(nodeId.replace('node-', ''), 10);
                if (isNaN(elementId))
                    continue;
                const element = elementMap.get(elementId);
                if (!element)
                    continue;
                // Вычисляем видимость элемента
                const elX = ((_a = element.props) === null || _a === void 0 ? void 0 : _a.x) || 0;
                const elY = ((_b = element.props) === null || _b === void 0 ? void 0 : _b.y) || 0;
                const elW = ((_c = element.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
                const elH = ((_d = element.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
                const elRight = elX + elW;
                const elBottom = elY + elH;
                // Проверяем пересечение с расширенной областью видимости
                const intersects = elRight >= searchLeft &&
                    elX <= searchRight &&
                    elBottom >= searchTop &&
                    elY <= searchBottom;
                // Проверяем, нужно ли обновить видимость
                const currentVisible = node.visible();
                if (currentVisible !== intersects) {
                    visibilityUpdates.push({ node, visible: intersects });
                }
            }
            // Применяем все обновления видимости батчем
            if (visibilityUpdates.length > 0) {
                const MAX_UPDATES_PER_FRAME = 150;
                const updatesToApply = visibilityUpdates.slice(0, MAX_UPDATES_PER_FRAME);
                // Батчим обновления видимости для лучшей производительности
                for (const { node, visible } of updatesToApply) {
                    node.visible(visible);
                }
                // Если есть еще обновления, планируем их на следующие кадры
                if (visibilityUpdates.length > MAX_UPDATES_PER_FRAME) {
                    const remainingUpdates = visibilityUpdates.slice(MAX_UPDATES_PER_FRAME);
                    // Разбиваем на несколько кадров для плавности
                    let updateIndex = 0;
                    const processNextBatch = () => {
                        const batchSize = 100;
                        const batch = remainingUpdates.slice(updateIndex, updateIndex + batchSize);
                        for (const { node, visible } of batch) {
                            node.visible(visible);
                        }
                        updateIndex += batchSize;
                        if (updateIndex < remainingUpdates.length) {
                            requestAnimationFrame(processNextBatch);
                        }
                        else {
                            const layer = currentStage.findOne('Layer');
                            if (layer && 'batchDraw' in layer && typeof layer.batchDraw === 'function') {
                                layer.batchDraw();
                            }
                        }
                    };
                    requestAnimationFrame(processNextBatch);
                }
                // Обновляем слой только если все обновления применены сразу
                if (visibilityUpdates.length <= MAX_UPDATES_PER_FRAME) {
                    const layer = currentStage.findOne('Layer');
                    if (layer && 'batchDraw' in layer && typeof layer.batchDraw === 'function') {
                        layer.batchDraw();
                    }
                }
            }
        };
        let rafId = null;
        let timeoutId = null;
        const UPDATE_INTERVAL_MS = 150; // Обновляем видимость каждые 150мс во время pan/zoom (увеличено для оптимизации)
        const IDLE_UPDATE_DELAY_MS = 300; // Задержка для обновления после окончания pan/zoom (увеличено для debouncing)
        /**
         * Планирует обновление видимости через requestAnimationFrame
         */
        const scheduleRAFUpdate = () => {
            if (rafId !== null)
                return; // Уже запланировано
            rafId = requestAnimationFrame(() => {
                rafId = null;
                updateVisibility();
            });
        };
        // Если активно pan/zoom, обновляем видимость периодически
        if (isPanning || isZooming) {
            // Очищаем предыдущие таймеры
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            // Запускаем периодическое обновление во время pan/zoom только если еще не запущено
            if (!visibilityUpdateIntervalRef.current) {
                lastVisibilityUpdateTimeRef.current = performance.now();
                // Первое обновление сразу при начале pan/zoom
                scheduleRAFUpdate();
                // Запускаем периодическое обновление с увеличенным интервалом для оптимизации FPS
                visibilityUpdateIntervalRef.current = setInterval(() => {
                    const now = performance.now();
                    // Обновляем только если прошло достаточно времени с последнего обновления
                    // Используем увеличенный интервал для предотвращения падений FPS
                    if (now - lastVisibilityUpdateTimeRef.current >= UPDATE_INTERVAL_MS) {
                        lastVisibilityUpdateTimeRef.current = now;
                        // Используем requestIdleCallback для неблокирующего обновления
                        if (typeof requestIdleCallback !== 'undefined') {
                            requestIdleCallback(() => {
                                scheduleRAFUpdate();
                            }, { timeout: 100 });
                        }
                        else {
                            scheduleRAFUpdate();
                        }
                    }
                }, UPDATE_INTERVAL_MS);
            }
        }
        else {
            // Если pan/zoom не активен, останавливаем интервал и обновляем видимость один раз
            if (visibilityUpdateIntervalRef.current) {
                clearInterval(visibilityUpdateIntervalRef.current);
                visibilityUpdateIntervalRef.current = null;
            }
            if (timeoutId)
                clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                if (typeof requestIdleCallback !== 'undefined') {
                    requestIdleCallback(updateVisibility, { timeout: 100 });
                }
                else {
                    scheduleRAFUpdate();
                }
            }, IDLE_UPDATE_DELAY_MS);
        }
        // Cleanup: очищаем только локальные таймеры, но НЕ интервал
        // Интервал должен продолжать работать во время pan/zoom
        return () => {
            // НЕ очищаем visibilityUpdateIntervalRef здесь - он очищается только при окончании pan/zoom
            if (rafId !== null)
                cancelAnimationFrame(rafId);
            if (timeoutId)
                clearTimeout(timeoutId);
        };
        // Зависимости только от состояния pan/zoom и boardState
        // НЕ включаем zoom, stagePosition, stageSize - они обновляются через refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardState.layers, isPanning, isZooming]);
};
